import { strings } from '@/strings';

/**
 * The server refuses by name — `cooldown`, `daily-limit`, `invalid-code` —
 * specifically so the UI can say something true. Collapsing everything into
 * "try again" would waste that, and "wrong code" for an expired one sends
 * people to retype a code that can never work.
 *
 * The two ceilings the caller did not cause (ip-limit, global-limit) are told
 * as a service problem rather than as their mistake, because it is.
 */
const MESSAGES: Record<string, string> = {
  cooldown: strings.errCooldown,
  'daily-limit': strings.errDailyLimit,
  'ip-limit': strings.errTryLater,
  'global-limit': strings.errTryLater,
  'send-failed': strings.errTryLater,
  'provider-error': strings.errTryLater,
  unparseable: strings.errTryLater,
  'invalid-phone': strings.errInvalidPhone,
  'invalid-code-format': strings.errWrongCode,
  'invalid-code': strings.errWrongCode,
  'too-many-attempts': strings.errTooManyAttempts,
  expired: strings.errCodeExpired,
  'not-found': strings.errCodeExpired,
  'not-a-recommender': strings.errNotARecommender,
  'name-required': strings.errNameRequired,
  'why-required': strings.errWhyRequired,
  'why-too-long': strings.errWhyTooLong,
  'already-resolved': strings.errAlreadyResolved,
  'not-interested': strings.errNotInterested,
  'already-a-candidate': strings.errAlreadyCandidate,
  'invalid-outcome': strings.errGeneric,
  'nomination-not-found': strings.errGeneric,
  'consent-required': strings.errConsentRequired,
  'competencies-required': strings.errCompetenciesRequired,
  'candidate-not-found': strings.errGeneric,
  'not-your-record': strings.errGeneric,
  'already-has-a-role': strings.errAlreadyHasRole,
  'already-registered': strings.errOrgAlreadyRegistered,
  'city-required': strings.errCityRequired,
  'mission-required': strings.errMissionRequired,
  'invalid-size': strings.errInvalidSize,
  'not-an-org-rep': strings.errGeneric,
  'not-your-org': strings.errGeneric,
  'org-not-found': strings.errGeneric,
  'no-org': strings.errGeneric,
  'priorities-required': strings.errPrioritiesRequired,
  'too-many-priorities': strings.errTooManyPriorities,
  'incomplete-coverage': strings.errIncompleteCoverage,
  'not-a-gap': strings.errNotAGap,
  'one-role-per-card': strings.errOneRolePerCard,
  'tasks-out-of-range': strings.errTasksRange,
  'task-required': strings.errTasksRange,
  'deliverable-required': strings.errTasksRange,
  'required-competencies': strings.errRequiredCompetencies,
  'too-many-required': strings.errTooManyRequired,
  'location-required': strings.errLocationRequired,
  'amount-required': strings.errAmountRequired,
  'duration-required': strings.errDurationRequired,
  'reports-to-required': strings.errReportsToRequired,
  'success-criteria-required': strings.errSuccessCriteria,
  'summary-required': strings.errSummaryRequired,
  'title-required': strings.errGeneric,
  'not-a-draft': strings.errNotADraft,
  'not-a-prioritized-gap': strings.errNotPrioritizedGap,
  'opportunity-not-found': strings.errGeneric,
  'invalid-decision': strings.errGeneric,
  'invalid-status': strings.errGeneric,
  'org-not-approved': strings.errOrgNotApproved,
  'already-proposed': strings.errAlreadyProposed,
  'not-a-match': strings.errNotAMatch,
  'not-vetted': strings.errNotVetted,
  'invalid-transition': strings.errInvalidTransition,
  'not-your-match': strings.errNotYourMatch,
  'not-live': strings.errNotLive,
  'match-not-found': strings.errGeneric,
  'accepted-must-be-boolean': strings.errGeneric,
  'admin-only-role': strings.errAdminOnlyRole,
  'invalid-role': strings.errInvalidRole,
  'cannot-change-own-role': strings.errCannotChangeOwnRole,
  'user-not-found': strings.errUserNotFound,
  'tag-not-found': strings.errTagNotFound,
  'nothing-to-change': strings.errNothingToChange,
  'invalid-tag': strings.errGeneric,
  'invalid-active': strings.errGeneric,
  'invalid-name': strings.errGeneric,
  'invalid-uid': strings.errGeneric,
};

/**
 * The server names some refusals with a detail attached — `unknown-tag:astrology`,
 * `invalid-stage:withdrawn` — so the prefix is what selects the message. Showing
 * the raw detail to an Arabic-speaking user would be worse than a generic line.
 */
export function messageForAuthError(code: string): string {
  const base = String(code || '').split(':')[0];
  if (base === 'invalid-stage') return strings.errInvalidStage;
  return MESSAGES[base] || strings.errGeneric;
}
